import { useState, useCallback, useRef } from 'react';
import { 
  getChatCompletion, 
  getStreamingChatCompletion, 
  getStructuredResponse, 
  validateConnection,
  enhancePrompt 
} from '../services/openai';

/**
 * Custom hook for OpenAI integration with enhanced error handling
 * @param {Object} initialConfig - Initial configuration
 * @returns {Object} Hook methods and state
 */
export const useOpenAI = (initialConfig = {}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [lastResponse, setLastResponse] = useState(null);
  const [organizationError, setOrganizationError] = useState(false);
  const streamingRef = useRef('');
  const [completedResponses, setCompletedResponses] = useState(new Map());
  const responseIdRef = useRef(0);

  // CRITICAL: Enhanced response stability system
  const [messageResponseRegistry, setMessageResponseRegistry] = useState(new Map());
  const [completionLock, setCompletionLock] = useState(new Set());
  const responseStabilityRef = useRef(new Map());

  // CRITICAL: Create stable response with immutability guarantee
  const createStableResponse = useCallback((responseData, responseId) => {
    const stableResponse = Object.freeze({
      ...responseData,
      id: responseId,
      stable: true,
      immutable: true,
      locked: true,
      timestamp: Date.now(),
      hash: `${responseId}-${responseData?.content?.substring(0, 30)}-${Date.now()}`
    });

    // CRITICAL: Register in stability system
    responseStabilityRef?.current?.set(responseId, stableResponse);
    setMessageResponseRegistry(prev => new Map(prev)?.set(responseId, stableResponse));
    setCompletionLock(prev => new Set(prev)?.add(responseId));

    return stableResponse;
  }, []);

  // Default configuration with stable production settings
  const [config, setConfig] = useState({
    model: 'gpt-4-turbo', // Use stable production model
    temperature: 0.7,
    maxTokens: 2048,
    systemPrompt: 'You are a helpful AI assistant.',
    streamEnabled: true,
    ...initialConfig
  });

  /**
   * CRITICAL: Send message with response stability guarantee
   */
  const sendMessage = useCallback(async (message, overrideConfig = {}) => {
    if (!message?.trim()) {
      setError('Message cannot be empty');
      return null;
    }

    // CRITICAL: Generate unique stable response ID
    const responseId = `response-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
    const startTime = Date.now();
    
    console.log(`[OpenAI] Starting stable completion for ${responseId}`);
    
    setIsLoading(true);
    setError(null);
    setOrganizationError(false);
    
    const requestConfig = { ...config, ...overrideConfig };

    try {
      const response = await getChatCompletion(message, requestConfig);
      
      if (response?.error) {
        if (response?.errorType === 'organization_verification') {
          setOrganizationError(true);
          setError('Organization verification required for advanced features');
        } else {
          setError(response?.errorMessage);
        }
      } else if (response?.content) {
        const endTime = Date.now();
        
        // CRITICAL: Create absolutely stable response
        const stableResponse = createStableResponse({
          content: response?.content,
          metadata: {
            responseTime: endTime - startTime,
            tokens: response?.metadata?.tokens || Math.floor(response?.content?.length / 4),
            model: response?.metadata?.model || requestConfig?.model,
            finishReason: response?.metadata?.finishReason,
            promptTokens: response?.metadata?.promptTokens || 0,
            completionTokens: response?.metadata?.completionTokens || 0,
            stable: true,
            locked: true
          }
        }, responseId);
        
        setLastResponse(stableResponse);
        setCompletedResponses(prev => new Map(prev)?.set(responseId, stableResponse));
        setOrganizationError(false);
        
        console.log(`[OpenAI] Stable completion created for ${responseId}`);
        return stableResponse;
      }
      
      return response;
    } catch (err) {
      const errorMessage = err?.message || 'An unexpected error occurred';
      setError(errorMessage);
      console.error(`[OpenAI] Error in stable completion ${responseId}:`, err);
      
      return { error: true, errorMessage, content: '', responseId };
    } finally {
      setIsLoading(false);
    }
  }, [config, createStableResponse]);

  /**
   * CRITICAL: Send streaming message with race condition protection
   */
  const sendStreamingMessage = useCallback(async (message, onChunk, overrideConfig = {}) => {
    if (!message?.trim()) {
      setError('Message cannot be empty');
      return null;
    }

    // CRITICAL: Generate unique streaming response ID
    const responseId = `stream-${Date.now()}-${Math.random()?.toString(36)?.substr(2, 9)}`;
    const startTime = Date.now();
    
    console.log(`[OpenAI] Starting stable streaming for ${responseId}`);
    
    setIsStreaming(true);
    setError(null);
    setOrganizationError(false);
    streamingRef.current = '';
    
    const requestConfig = { ...config, ...overrideConfig };
    let streamActive = true;
    let streamingContent = '';

    try {
      const response = await getStreamingChatCompletion(
        message, 
        (chunk) => {
          // CRITICAL: Only process if stream is active and not locked
          if (streamActive && !completionLock?.has(responseId)) {
            streamingContent += chunk;
            streamingRef.current = streamingContent;
            onChunk(chunk, streamingContent);
          }
        }, 
        requestConfig
      );
      
      // CRITICAL: Lock stream completion to prevent race conditions
      streamActive = false;
      setCompletionLock(prev => new Set(prev)?.add(responseId));
      
      const endTime = Date.now();
      
      if (response?.error) {
        if (response?.errorType === 'organization_verification') {
          setOrganizationError(true);
          setError('Streaming requires organization verification - using standard mode');
        } else {
          setError(response?.errorMessage);
        }
      } else if (response?.content) {
        // CRITICAL: Create stable streaming response
        const stableResponse = createStableResponse({
          content: response?.content,
          metadata: {
            responseTime: endTime - startTime,
            tokens: response?.metadata?.tokens || Math.floor(streamingContent?.length / 4),
            model: response?.metadata?.model || requestConfig?.model,
            streaming: true,
            stable: true,
            locked: true,
            finalContent: streamingContent
          }
        }, responseId);
        
        setLastResponse(stableResponse);
        setCompletedResponses(prev => new Map(prev)?.set(responseId, stableResponse));
        setOrganizationError(false);
        
        console.log(`[OpenAI] Stable streaming completion created for ${responseId}`);
        
        // Show info message if fallback was used
        if (response?.metadata?.fallback) {
          console.info('Streaming fallback was used due to organization verification requirements');
        }
        
        return stableResponse;
      }
      
      return response;
    } catch (err) {
      streamActive = false; // CRITICAL: Stop stream processing
      setCompletionLock(prev => new Set(prev)?.add(responseId));
      
      const errorMessage = err?.message || 'An unexpected error occurred during streaming';
      setError(errorMessage);
      console.error(`[OpenAI] Error in stable streaming ${responseId}:`, err);
      
      onChunk('', `Error: ${errorMessage}`);
      return { error: true, errorMessage, content: '', responseId };
    } finally {
      setIsStreaming(false);
    }
  }, [config, createStableResponse, completionLock]);

  /**
   * Get structured response from OpenAI
   * @param {string} message - User message
   * @param {Object} schema - JSON schema for response structure
   * @param {Object} overrideConfig - Override configuration
   * @returns {Promise<Object>} Structured response
   */
  const getStructured = useCallback(async (message, schema, overrideConfig = {}) => {
    if (!message?.trim()) {
      setError('Message cannot be empty');
      return null;
    }

    setIsLoading(true);
    setError(null);
    setOrganizationError(false);
    
    const requestConfig = { ...config, ...overrideConfig };

    try {
      const response = await getStructuredResponse(message, schema, requestConfig);
      
      if (response?.error) {
        setError(`Structured response error: ${response?.errorMessage || 'Unknown error'}`);
      } else {
        setLastResponse(response);
      }
      
      return response;
    } catch (err) {
      const errorMessage = err?.message || 'An unexpected error occurred with structured response';
      setError(errorMessage);
      return { error: true, errorMessage, data: null };
    } finally {
      setIsLoading(false);
    }
  }, [config]);

  /**
   * Enhance a prompt using OpenAI
   * @param {string} prompt - Original prompt
   * @param {string} context - Additional context
   * @returns {Promise<string>} Enhanced prompt
   */
  const enhanceUserPrompt = useCallback(async (prompt, context = '') => {
    if (!prompt?.trim()) return prompt;

    try {
      const enhanced = await enhancePrompt(prompt, context);
      return enhanced;
    } catch (err) {
      console.error('Failed to enhance prompt:', err);
      return prompt; // Return original on error
    }
  }, []);

  /**
   * Test connection to OpenAI with better error reporting
   * @returns {Promise<Object>} Connection status
   */
  const testConnection = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    setOrganizationError(false);

    try {
      const status = await validateConnection();
      setConnectionStatus(status);
      
      if (!status?.connected) {
        if (status?.errorType === 'organization_verification') {
          setOrganizationError(true);
          setError('Organization verification required - some features may be limited');
        } else {
          setError(status?.error);
        }
      }
      
      return status;
    } catch (err) {
      const errorMessage = err?.message || 'Connection test failed';
      setError(errorMessage);
      setConnectionStatus({ connected: false, error: errorMessage });
      return { connected: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Update configuration with safe model mapping
   * @param {Object} newConfig - New configuration values
   */
  const updateConfig = useCallback((newConfig) => {
    setConfig(prev => {
      const updatedConfig = { ...prev, ...newConfig };
      
      // Map potentially problematic models to safe alternatives
      if (updatedConfig?.model?.includes('gpt-5')) {
        updatedConfig.model = updatedConfig?.model?.includes('nano') ? 'gpt-3.5-turbo' : 'gpt-4';
      }
      
      return updatedConfig;
    });
  }, []);

  /**
   * CRITICAL: Clear all response history to prevent stale references
   */
  const clearAllResponses = useCallback(() => {
    console.log('[OpenAI] Clearing all responses with stability reset');
    
    setCompletedResponses(new Map());
    setMessageResponseRegistry(new Map());
    setCompletionLock(new Set());
    setLastResponse(null);
    responseIdRef.current = 0;
    streamingRef.current = '';
    responseStabilityRef?.current?.clear();
  }, []);

  /**
   * Reset error state and clear stale responses
   */
  const clearError = useCallback(() => {
    setError(null);
    setOrganizationError(false);
  }, []);

  /**
   * Get current streaming content
   */
  const getCurrentStreamingContent = useCallback(() => {
    return streamingRef?.current;
  }, []);

  return {
    // State
    isLoading,
    isStreaming,
    error,
    connectionStatus,
    lastResponse,
    config,
    organizationError,
    completedResponses, // CRITICAL: Stable response history
    
    // Methods
    sendMessage,
    sendStreamingMessage,
    getStructured,
    enhanceUserPrompt,
    testConnection,
    updateConfig,
    clearError,
    clearAllResponses, // CRITICAL: Enhanced clearing method
    getCurrentStreamingContent,
    
    // CRITICAL: Enhanced stability tracking
    messageResponseRegistry,
    completionLock,
    
    // Computed values
    isConnected: connectionStatus?.connected ?? null,
    hasError: !!error,
    hasOrganizationError: organizationError,
  };
};

export default useOpenAI;